



import React, { useState, useEffect, useRef } from 'react';
import {
  Menu, X, ChevronDown, Heart, Activity,
  Shield, Wind, Smile, FileText, Grid3x3, Eye, Stethoscope, HeartPulse, CreditCard
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { encryptData } from "../../api/Services/encryptData";
import logowithname from "../../Assets/Images/logowithname.svg";
import axios from 'axios';
import { useAuthContext } from '../../auth';
import { clearUserSession } from '../../auth/utils/storage';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [scrollY, setScrollY] = useState(false);

  const navigate = useNavigate();
  const { openAuth } = useAuthContext();

  const services = [
    { name: 'CareSathi',       description: 'AI-powered personal health companion',    icon: Heart,        gradient: 'from-rose-400 to-pink-500',    url: '/caresathi' },
    { name: 'SmartVitals',     description: 'Real-time vital signs monitoring',         icon: Activity,     gradient: 'from-emerald-400 to-teal-500', url: '/smartvital' },
    { name: 'SkinCare',        description: 'Dermatological AI analysis',               icon: Shield,       gradient: 'from-green-400 to-emerald-500',url: '/skincare' },
    { name: 'LungsCare',       description: 'Respiratory health monitoring',            icon: Wind,         gradient: 'from-cyan-400 to-blue-500',    url: '/lungscare' },
    { name: 'OralCare',        description: 'Dental health using computer vision',      icon: Smile,        gradient: 'from-yellow-400 to-orange-500',url: '/oralcare' },
    { name: 'Smart Report',    description: 'Automated medical report generation',      icon: FileText,     gradient: 'from-primary-start to-primary-end',url: '/smartReport' },
    { name: 'Smart Diet',      description: 'Personalised nutrition guidance',          icon: HeartPulse,   gradient: 'from-blue-400 to-[#004276]',  url: '/smartdiet' },
    { name: 'Symptom Checker', description: 'AI-based symptom analysis',               icon: Stethoscope,  gradient: 'from-blue-400 to-[#004276]',  url: '/symptomchecker' },
    { name: 'VisionCare',      description: 'AI-powered vision analysis',              icon: Eye,          gradient: 'from-blue-400 to-indigo-500',  url: '/visioncare' },
  ];

  const endpointBase = process.env.REACT_APP_Curebay;

  useEffect(() => {
    const fetchToken = async () => {
      try {
        let jsonData = {
          userCode: process.env.REACT_APP_USER_CODE,
          passWord: process.env.REACT_APP_USER_PASSWORD,
          channel: 'M'
        };
        let encryptedPayload = encryptData(jsonData);
        const result = await axios.post(
          process.env.REACT_APP_BASEURL + process.env.REACT_APP_TOKEN,
          encryptedPayload
        );
        sessionStorage.setItem("token", result?.data?.access_token);
        localStorage.setItem("token", result?.data?.access_token);
      } catch (error) {
        console.error('Error fetching token:', error);
      }
    };
    fetchToken();
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isServicesOpen && !event.target.closest('.services-dropdown')) setIsServicesOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isServicesOpen]);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMenuOpen]);

  const handleNavigation = (path) => {
    navigate(path);
    setIsMenuOpen(false);
    setIsServicesOpen(false);
  };

  const handleServiceClick = (service) => {
    setIsMenuOpen(false);
    setIsServicesOpen(false);
    navigate(service.url);
  };

  const servicesDropdownRef = useRef(null);

  const handleLogout = () => {
    clearUserSession();
    setIsProfileOpen(false);
    navigate('/');
  };
  const HOME_URL = process.env.REACT_APP_HOME_URL;

const handleLogoClick = () => {
  if (HOME_URL) {
    window.location.href = HOME_URL; // full redirect
  } else {
    console.warn("HOME URL not set in .env");
  }
};

  const getPatientData = () => {
    try { return JSON.parse(localStorage.getItem('loginObj') || 'null'); } catch { return null; }
  };
  const [patientData, setPatientData] = useState(getPatientData);

  useEffect(() => {
    const refresh = () => setPatientData(getPatientData());
    window.addEventListener('loginComplete', refresh);
    window.addEventListener('logoutComplete', refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener('loginComplete', refresh);
      window.removeEventListener('logoutComplete', refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);

  const userName = patientData
    ? (patientData.patientName ||
       patientData.name ||
       (patientData.firstName
         ? `${patientData.firstName}${patientData.lastName ? ' ' + patientData.lastName : ''}`
         : null) ||
       patientData.mobile ||
       null)
    : null;
  const userPhone = patientData?.mobile || patientData?.phone || null;

  return (
    <>
      <header className="fixed top-0 w-full z-50 bg-white shadow-[0px_4px_10px_0px_#80DAF180]">
        {/* Main bar */}
        <div className="w-full px-6 md:px-10 xl:px-[max(60px,calc(50%_-_660px))] h-14 flex items-center justify-between gap-6">

          {/* Logo */}
          <div className="cursor-pointer flex-shrink-0" onClick={handleLogoClick}>
            <img src={logowithname} alt="CureBay" className="h-8 w-auto" />
          </div>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1 flex-1 justify-center">
            <button
              onClick={() => handleNavigation('/')}
              className="px-4 py-2 text-sm font-bold text-[#004276] hover:text-[#00BD7F] transition-colors"
            >
              Home
            </button>

            {/* Our Services dropdown */}
            <div className="relative services-dropdown" ref={servicesDropdownRef}>
              <button
                onClick={() => setIsServicesOpen(!isServicesOpen)}
                className="flex items-center gap-1 px-4 py-2 text-sm font-semibold text-[#004276] hover:text-[#00BD7F] transition-colors"
              >
                Our Services
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isServicesOpen ? 'rotate-180' : ''}`} />
              </button>

              {isServicesOpen && (
                <div className="absolute top-full left-0 mt-2 w-[560px] bg-white border border-gray-100 rounded-2xl shadow-2xl p-4 z-50 grid grid-cols-2 gap-2">
                  {services.map((service) => {
                    const Icon = service.icon;
                    return (
                      <button
                        key={service.name}
                        onClick={() => handleServiceClick(service)}
                        className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 text-left transition-colors"
                      >
                        <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${service.gradient} flex items-center justify-center flex-shrink-0 shadow-sm`}>
                          <Icon className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <p className="font-semibold text-[#004276] text-sm">{service.name}</p>
                          <p className="text-xs text-gray-400 leading-snug">{service.description}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <button
              onClick={() => handleNavigation('/documents?tab=reports')}
              className="px-4 py-2 text-sm font-semibold text-[#004276] hover:text-[#00BD7F] transition-colors"
            >
              Validation Reports
            </button>

            <button
             onClick={() => handleNavigation('/documents?tab=papers')}
              className="px-4 py-2 text-sm font-semibold text-[#004276] hover:text-[#00BD7F] transition-colors"
            >
              Research Publications
            </button>
          </nav>

          {/* Right: User info */}
          <div className="hidden md:flex items-center gap-3 flex-shrink-0">
            {userName ? (
              <div
                className="flex items-center justify-center gap-2 cursor-pointer group"
                onClick={() => setIsProfileOpen(true)}
              >
                <div className="w-8 h-8 rounded-full bg-[#D6EEFB] flex items-center justify-center text-[#004276] text-xs font-bold flex-shrink-0">
                  {userName.charAt(0).toUpperCase()}
                </div>
                <div className="leading-tight">
                  <p className="text-sm font-semibold text-[#004276] group-hover:text-[#00BD7F] transition-colors mb-0">
                    {userName}
                  </p>
                  {userPhone && (
                    <p className="text-xs text-gray-400 mb-0">{userPhone}</p>
                  )}
                </div>
                <ChevronDown className="w-4 h-4 text-gray-400" />
              </div>
            ) : (
              <button
                onClick={() => openAuth('login')}
                className="px-5 py-2 text-sm font-semibold bg-[#004276] text-white rounded-full hover:bg-[#003360] transition-colors shadow-sm"
              >
                Login
              </button>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 text-gray-700 transition-colors"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {/* ── Mobile Drawer ── */}
        <div className={`md:hidden fixed inset-0 z-50 transition-all duration-300 ${isMenuOpen ? 'pointer-events-auto' : 'pointer-events-none'}`}>
          <div
            className={`absolute inset-0 bg-black/40 transition-opacity duration-300 ${isMenuOpen ? 'opacity-100' : 'opacity-0'}`}
            onClick={() => setIsMenuOpen(false)}
          />
          <div className={`absolute top-0 right-0 h-full w-[80vw] max-w-[320px] bg-white shadow-2xl flex flex-col transition-transform duration-300 ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>

            {/* Drawer header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <img src={logowithname} alt="CureBay" className="h-7 w-auto" />
              <button onClick={() => setIsMenuOpen(false)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-700">
                <X className="h-5 w-5" />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto px-4 py-4 space-y-1">
              {/* Home */}
              <button onClick={() => handleNavigation('/')} className="w-full text-left py-3 px-4 rounded-xl hover:bg-slate-50 text-[#004276] font-medium">
                Home
              </button>

              {/* Validation Reports */}
              <button onClick={() => handleNavigation('/documents?tab=reports')} className="w-full text-left py-3 px-4 rounded-xl hover:bg-slate-50 text-[#004276] font-medium">
                Validation Reports
              </button>

              {/* Research Publications */}
              <button onClick={() => handleNavigation('/documents?tab=papers')} className="w-full text-left py-3 px-4 rounded-xl hover:bg-slate-50 text-[#004276] font-medium">
                Research Publications
              </button>

              {/* FIX 2: Profile account links shown in mobile drawer when logged in */}
              {userName && (
                <>
                  <div className="pt-3 pb-1 px-4">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">My Account</p>
                  </div>

                  <button
                    onClick={() => { handleNavigation('/my-subscription'); }}
                    className="w-full flex items-center gap-3 py-3 px-4 rounded-xl hover:bg-[#EAF3FA] text-[#004276] font-medium text-sm"
                  >
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-start to-primary-end flex items-center justify-center flex-shrink-0">
                      <CreditCard className="w-4 h-4 text-white" />
                    </div>
                    My Subscription
                  </button>

                  <button
                    onClick={() => { handleNavigation('/aireports'); }}
                    className="w-full flex items-center gap-3 py-3 px-4 rounded-xl hover:bg-[#EAF3FA] text-[#004276] font-medium text-sm"
                  >
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-start to-primary-end flex items-center justify-center flex-shrink-0">
                      <FileText className="w-4 h-4 text-white" />
                    </div>
                    My AI Reports
                  </button>

                  <button
                    onClick={() => { handleNavigation('/my-smart-diets'); }}
                    className="w-full flex items-center gap-3 py-3 px-4 rounded-xl hover:bg-[#EAF3FA] text-[#004276] font-medium text-sm"
                  >
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-start to-primary-end flex items-center justify-center flex-shrink-0">
                      <FileText className="w-4 h-4 text-white" />
                    </div>
                    My Smart Diets
                  </button>

                  <button
                    onClick={() => { handleNavigation('/smartreportlist'); }}
                    className="w-full flex items-center gap-3 py-3 px-4 rounded-xl hover:bg-[#EAF3FA] text-[#004276] font-medium text-sm"
                  >
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-start to-primary-end flex items-center justify-center flex-shrink-0">
                      <FileText className="w-4 h-4 text-white" />
                    </div>
                    My Smart Reports
                  </button>
                </>
              )}
            </nav>

            {/* User info strip at bottom */}
            {userName ? (
              <div className="border-t border-gray-100 bg-slate-50">
                <div className="px-5 py-3 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-[#D6EEFB] flex items-center justify-center text-[#004276] text-sm font-bold flex-shrink-0">
                    {userName.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#004276] truncate">{userName}</p>
                    {userPhone && <p className="text-xs text-gray-400">{userPhone}</p>}
                  </div>
                </div>
                {/* Logout in mobile drawer */}
                <div className="px-4 pb-4">
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-red-50 hover:bg-red-100 text-red-600 font-semibold text-sm transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                      <polyline points="16 17 21 12 16 7" />
                      <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                    Logout
                  </button>
                </div>
              </div>
            ) : (
              <div className="border-t border-gray-100 px-4 py-4">
                <button
                  onClick={() => { setIsMenuOpen(false); openAuth('login'); }}
                  className="w-full px-5 py-2.5 text-sm font-semibold bg-[#004276] text-white rounded-full hover:bg-[#003360] transition-colors shadow-sm"
                >
                  Login
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* ── Profile Sidebar (desktop) ── */}
      <div className={`fixed inset-0 z-[60] transition-all duration-300 ${isProfileOpen ? 'pointer-events-auto' : 'pointer-events-none'}`}>
        <div
          className={`absolute inset-0 bg-black/40 transition-opacity duration-300 ${isProfileOpen ? 'opacity-100' : 'opacity-0'}`}
          onClick={() => setIsProfileOpen(false)}
        />
        <div className={`absolute top-0 right-0 h-full w-72 bg-white shadow-2xl flex flex-col transition-transform duration-300 ${isProfileOpen ? 'translate-x-0' : 'translate-x-full'}`}>
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <span className="font-semibold text-[#004276] text-base">My Account</span>
            <button onClick={() => setIsProfileOpen(false)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>

          <nav className="flex-1 px-3 py-4 space-y-1">
            <button
              onClick={() => { handleNavigation('/my-subscription'); setIsProfileOpen(false); }}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-[#EAF3FA] text-[#004276] hover:text-[#00BD7F] font-medium text-sm transition-colors text-left"
            >
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-start to-primary-end flex items-center justify-center flex-shrink-0">
                <CreditCard className="w-4 h-4 text-white" />
              </div>
              My Subscription
            </button>
            <button
              onClick={() => { handleNavigation('/aireports'); setIsProfileOpen(false); }}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-[#EAF3FA] text-[#004276] hover:text-[#00BD7F] font-medium text-sm transition-colors text-left"
            >
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-start to-primary-end flex items-center justify-center flex-shrink-0">
                <FileText className="w-4 h-4 text-white" />
              </div>
              My AI Reports
            </button>
            <button
              onClick={() => { handleNavigation('/my-smart-diets'); setIsProfileOpen(false); }}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-[#EAF3FA] text-[#004276] hover:text-[#00BD7F] font-medium text-sm transition-colors text-left"
            >
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-start to-primary-end flex items-center justify-center flex-shrink-0">
                <FileText className="w-4 h-4 text-white" />
              </div>
              My Smart Diets
            </button>
            <button
              onClick={() => { handleNavigation('/smartreportlist'); setIsProfileOpen(false); }}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-[#EAF3FA] text-[#004276] hover:text-[#00BD7F] font-medium text-sm transition-colors text-left"
            >
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-start to-primary-end flex items-center justify-center flex-shrink-0">
                <FileText className="w-4 h-4 text-white" />
              </div>
              My Smart Reports
            </button>
          </nav>

          <div className="px-4 py-4 border-t border-gray-100">
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-red-50 hover:bg-red-100 text-red-600 font-semibold text-sm transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Spacer */}
      <div className="h-[58px]" />
    </>
  );
};

export default Header;